var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import * as React from "react";
import { cva } from "class-variance-authority";
import { Loader2 } from "lucide-react";
import { cn } from "../../lib/utils.js";
var textColorFallbacks = {
    // Lime primary needs near-black label text in light and dark (avoid host token drift to white).
    default: 'hsl(0 0% 14%)',
    primary: 'hsl(0 0% 14%)',
    destructive: 'hsl(var(--destructive-foreground, 24 50% 96%))',
    outline: 'hsl(var(--outline-foreground, 0 0% 24%))',
    secondary: 'hsl(var(--secondary-foreground, 0 0% 14%))',
    // Readable on muted surfaces in light + dark (avoid near-invisible disabled labels).
    disabled: 'hsl(var(--foreground) / 0.55)',
    success: '#FAF4F0',
};
var buttonVariants = cva(
// Opacity for disabled is handled by the dedicated `disabled` variant (not opacity-50),
// so dark-mode labels stay readable on muted backgrounds.
"inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none disabled:pointer-events-none [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0", {
    variants: {
        variant: {
            // Hardcoded near-black label — never use !text-primary-foreground.
            // Host/MFE shells often drift --primary-foreground to near-white while
            // keeping lime --primary, which washes out CTA text on the brand green.
            default: 'bg-primary !text-[hsl(0_0%_14%)] dark:!text-[hsl(0_0%_10%)] shadow hover:bg-primary-hover active:bg-primary-pressed',
            destructive: 'bg-destructive text-destructive-foreground shadow-sm hover:bg-destructive-hover active:bg-destructive-pressed',
            outline: 'border text-outline-foreground border-input bg-background shadow-sm hover:bg-accent hover:text-accent-foreground active:bg-accent-pressed',
            secondary: 'bg-secondary text-secondary-foreground shadow-sm hover:bg-secondary-hover active:bg-secondary-pressed',
            ghost: 'text-foreground hover:bg-accent hover:text-accent-foreground',
            link: 'underline-offset-4 underline decoration-link-border hover:decoration-primary-foreground',
            disabled: 'bg-muted text-foreground/55 border border-border shadow-none dark:bg-zinc-800 dark:text-zinc-300 dark:border-zinc-700',
            primary: 'bg-primary !text-[hsl(0_0%_14%)] dark:!text-[hsl(0_0%_10%)] shadow hover:bg-primary-hover active:bg-primary-pressed',
            success: 'bg-[#009735] dark:bg-success text-[#FAF4F0] dark:text-background shadow hover:bg-[#27B85A] dark:hover:bg-success/80 active:bg-[#006623] dark:active:bg-success-foreground',
        },
        size: {
            default: 'h-9 px-4 py-2 rounded-lg',
            sm: 'h-8 rounded-lg px-3 text-xs',
            md: 'h-10 rounded-lg px-3',
            lg: 'h-10 rounded-lg px-8',
            icon: 'h-9 w-9 rounded-lg',
        },
    },
    defaultVariants: {
        variant: "default",
        size: "default",
    },
});
function getTextColorStyle(variant) {
    var key = variant || 'default';
    var color = textColorFallbacks[key];
    return color ? { color: color } : undefined;
}
var Button = React.forwardRef(function (_a, ref) {
    var className = _a.className, variant = _a.variant, size = _a.size, style = _a.style, _b = _a.asChild, asChild = _b === void 0 ? false : _b, disabled = _a.disabled, loading = _a.loading, children = _a.children, props = __rest(_a, ["className", "variant", "size", "style", "asChild", "disabled", "loading", "children"]);
    var effectiveVariant = (disabled || loading) ? 'disabled' : (variant || 'default');
    var fallbackStyle = getTextColorStyle(effectiveVariant);
    if (asChild && React.isValidElement(children)) {
        var cloned = React.cloneElement(children, __assign({ className: cn(buttonVariants({ variant: effectiveVariant, size: size }), children.props.className), style: __assign(__assign(__assign({}, fallbackStyle), style), children.props.style), disabled: disabled }, props));
        return cloned;
    }
    var Comp = 'button';
    return (_jsxs(Comp, __assign({ className: cn(buttonVariants({ variant: effectiveVariant, size: size }), className), ref: ref, disabled: disabled || loading, style: __assign(__assign({}, fallbackStyle), style) }, props, { children: [loading && (_jsx(Loader2, { className: "animate-spin h-4 w-4" })), children] })));
});
Button.displayName = "Button";
export { Button, buttonVariants };
